import React, { useState, useEffect } from 'react';
import { 
    SparklesIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';

const AiTips = ({ cvData, sector, isVisible = true, onClose = () => {}, onNavigateToSection = () => {} }) => {
    const [tips, setTips] = useState([]);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (isVisible && cvData) {
            generateTips();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cvData, sector, isVisible]);

    const generateTips = async () => {
        setIsLoading(true);
        
        // Simulation de génération de conseils basés sur le CV
        // En production, cela viendrait de l'API IA
        const generatedTips = analyzeCVAndGenerateTips(cvData, sector);
        
        setTimeout(() => {
            setTips(generatedTips);
            setIsLoading(false);
        }, 1000);
    };

    const analyzeCVAndGenerateTips = (cvData, sector) => {
        const tips = [];

        // Analyse de la structure
        if (!cvData.summary || cvData.summary.length < 50) {
            tips.push({
                id: 'summary',
                type: 'warning',
                title: 'Résumé manquant ou trop court',
                description: 'Ajoutez un résumé professionnel de 2-3 phrases pour captiver le recruteur dès le début.',
                action: 'Ajouter un résumé'
            });
        }

        if (!cvData.experience || cvData.experience.length === 0) {
            tips.push({
                id: 'experience',
                type: 'error',
                title: 'Aucune expérience renseignée',
                description: 'Même sans expérience professionnelle, vous pouvez ajouter des stages, projets ou bénévolat.',
                action: 'Ajouter des expériences'
            });
        }

        if (!cvData.skills || cvData.skills.length < 5) {
            tips.push({
                id: 'skills',
                type: 'info',
                title: 'Compétences à enrichir',
                description: 'Ajoutez plus de compétences techniques et soft skills pour améliorer votre visibilité.',
                action: 'Enrichir les compétences'
            });
        }

        // Conseils sectoriels
        if (sector) {
            const sectorTips = getSectorTips(sector, cvData);
            tips.push(...sectorTips);
        }

        // Conseils de formatage
        if (cvData.experience?.length > 0) {
            const hasQuantifiedResults = cvData.experience.some(exp => 
                exp.description && /[0-9]/.test(exp.description)
            );
            
            if (!hasQuantifiedResults) {
                tips.push({
                    id: 'quantification',
                    type: 'success',
                    title: 'Quantifiez vos réalisations',
                    description: 'Ajoutez des chiffres et pourcentages pour rendre vos expériences plus impactantes.',
                    action: 'Améliorer les descriptions'
                });
            }
        }

        return tips.slice(0, 4); // Limiter à 4 conseils
    };

    const getSectorTips = (sector, cvData) => {
        const sectorTips = {
            'informatique': [
                {
                    id: 'tech-projects',
                    type: 'info',
                    title: 'Projets techniques',
                    description: 'Ajoutez vos projets GitHub, contributions open source ou applications développées.',
                    action: 'Ajouter des projets'
                }
            ],
            'marketing': [
                {
                    id: 'marketing-kpis',
                    type: 'success',
                    title: 'KPI marketing',
                    description: 'Mettez en avant vos résultats : ROI, taux de conversion, croissance du trafic.',
                    action: 'Quantifier les résultats'
                }
            ],
            'finance': [
                {
                    id: 'finance-certifications',
                    type: 'warning',
                    title: 'Certifications financières',
                    description: 'Ajoutez vos certifications (CFA, FRM, etc.) pour renforcer votre crédibilité.',
                    action: 'Ajouter les certifications'
                }
            ]
        };

        return sectorTips[sector] || [];
    };

    const getTipIcon = (type) => {
        switch (type) {
            case 'error':
                return '🔴';
            case 'warning':
                return '🟡';
            case 'success':
                return '🟢';
            default:
                return '💡';
        }
    };

    const getTipColor = (type) => {
        switch (type) {
            case 'error':
                return 'border-red-200 bg-red-50';
            case 'warning':
                return 'border-yellow-200 bg-yellow-50';
            case 'success':
                return 'border-green-200 bg-green-50';
            default:
                return 'border-blue-200 bg-blue-50';
        }
    };

    if (!isVisible || tips.length === 0) return null;

    return (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                            <SparklesIcon className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="font-semibold">Conseils IA</h3>
                            <p className="text-sm text-blue-100">
                                {isLoading ? 'Analyse en cours...' : `${tips.length} conseils personnalisés`}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="p-1 hover:bg-white/20 rounded-full transition-colors"
                        >
                            {isExpanded ? (
                                <ChevronUpIcon className="w-5 h-5" />
                            ) : (
                                <ChevronDownIcon className="w-5 h-5" />
                            )}
                        </button>
                        <button
                            onClick={onClose}
                            className="p-1 hover:bg-white/20 rounded-full transition-colors"
                        >
                            <XMarkIcon className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Content */}
            {isExpanded && (
                <div className="p-4 space-y-3">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            <span className="ml-3 text-gray-600">Analyse de votre CV...</span>
                        </div>
                    ) : (
                        tips.map((tip) => (
                            <div
                                key={tip.id}
                                className={`p-4 rounded-xl border ${getTipColor(tip.type)}`}
                            >
                                <div className="flex items-start gap-3">
                                    <span className="text-2xl">{getTipIcon(tip.type)}</span>
                                    <div className="flex-1">
                                        <h4 className="font-semibold text-gray-800 mb-1">
                                            {tip.title}
                                        </h4>
                                        <p className="text-sm text-gray-600 mb-3">
                                            {tip.description}
                                        </p>
                                        <button 
                                            onClick={() => onNavigateToSection(tip.id)}
                                            className="text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline transition-colors"
                                        >
                                            {tip.action} →
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default AiTips;
